import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';


// Бренд админки — ТОЛЬКО из env инстанса, без дефолтов: не задан — явная ошибка конфигурации.
const BRAND_KEY = (process.env.REACT_APP_BRAND || '').toLowerCase();
const BRAND_NAME = process.env.REACT_APP_BRAND_NAME || '';
const BRAND_THEME_COLOR = process.env.REACT_APP_THEME_COLOR || '';
const BRAND_TAGLINE = process.env.REACT_APP_BRAND_TAGLINE || '';
if (!BRAND_KEY || !BRAND_NAME || !BRAND_THEME_COLOR) {
  throw new Error(
    'Админка собрана без бренд-переменных (REACT_APP_BRAND / REACT_APP_BRAND_NAME / REACT_APP_THEME_COLOR) — задайте их в build-args инстанса'
  );
}

// Лого бренда: картинка с витринного домена (единственный экземпляр файла);
// если файла нет (текстовый бренд) — имя текстом.
function BrandLogo() {
  const [imgFailed, setImgFailed] = React.useState(false);
  const storefrontOrigin = window.location.origin.replace('//admin.', '//');
  if (imgFailed) {
    return <div className="login-stems-wordmark" style={{ fontWeight: 900, fontSize: 34, letterSpacing: 2 }}>{BRAND_NAME}</div>;
  }
  return (
    <img
      src={`${storefrontOrigin}/brands/${BRAND_KEY}/logo.svg`}
      alt={BRAND_NAME}
      className="login-stems-wordmark"
      onError={() => setImgFailed(true)}
    />
  );
}

// Пауза входа растёт до 15 минут — от минуты показываем «м:сс»
function formatWait(sec) {
  if (sec < 60) return `${sec} с`;
  return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
}

// Секундомер обратного отсчёта: [осталось, запустить(секунд)]
function useCountdown() {
  const [sec, setSec] = useState(0);
  useEffect(() => {
    if (sec <= 0) return undefined;
    const t = setTimeout(() => setSec((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [sec]);
  return [sec, setSec];
}

const CHANNEL_LABELS = { telegram: 'Telegram', max: 'Max' };

// Те же правила, что на сервере (backend/app/services/staff_passwords.py) — здесь
// только подсказка по мере ввода, решает сервер
function passwordRules(pw) {
  return [
    { text: 'не короче 7 символов', ok: pw.length >= 7 },
    { text: 'заглавная буква', ok: /\p{Lu}/u.test(pw) },
    { text: 'цифра', ok: /\d/.test(pw) },
    { text: 'спецсимвол: ! @ # $ % & * - _ ?', ok: /[^\p{L}\p{N}\s]/u.test(pw) },
  ];
}

const errorStyle = { color: 'var(--danger-color)', fontSize: '14px', marginBottom: '16px' };
const hintStyle = { color: '#666', fontSize: '14px', marginBottom: '16px' };

function LoginShell({ title, children }) {
  return (
    <div className="login-page">
      <div className="login-card">
        {/* Бренд клиента — из env инстанса (те же переменные, что у витрины).
            Лого-картинка берётся с витринного домена (единственный экземпляр файла
            в public/brands/<brand>/); если её нет (текстовый бренд, как Стемс) —
            фолбэк рендерит имя текстом. */}
        <div className="login-stems-header" style={{ background: BRAND_THEME_COLOR }}>
          <BrandLogo />
          {BRAND_TAGLINE && <div className="login-stems-tagline">{BRAND_TAGLINE}</div>}
        </div>

        <div className="login-form-body">
          <h1 className="login-form-title">{title}</h1>
          {children}
        </div>

        {/* Платформа NovaFlo */}
        <div className="login-novaflo-footer">
          <img src="/logo/novaflo-logo-v2.svg?v=2" alt="NovaFlo" />
        </div>
      </div>
    </div>
  );
}

// Глазок: показать/скрыть набранный пароль (особенно при смене — новые правила
// со спецсимволом, легко опечататься)
const EyeIcon = ({ open }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" />
    <circle cx="12" cy="12" r="3" />
    {!open && <path d="M3 3l18 18" />}
  </svg>
);

function PasswordInput({ id, value, onChange, placeholder, autoComplete, autoFocus }) {
  const [visible, setVisible] = useState(false);
  return (
    // Контур и срезы рисует обёртка: автозаполнение (Face ID, сохранённые
    // пароли) снимает фон у самого поля, а с ним и диагонали
    <div className="nf-input-wrap" style={{ position: 'relative' }}>
      <input
        id={id}
        type={visible ? 'text' : 'password'}
        className="form-input"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        style={{ paddingRight: 48 }}
        required
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? 'Скрыть пароль' : 'Показать пароль'}
        title={visible ? 'Скрыть пароль' : 'Показать пароль'}
        style={{
          position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', zIndex: 2,
          width: 36, height: 36, padding: 0, border: 'none', background: 'transparent',
          color: visible ? '#1C3D2E' : '#909894', cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <EyeIcon open={visible} />
      </button>
    </div>
  );
}

// После входа по временному паролю (или по паролю слабее правил) — задать свой
function ChangePasswordForm() {
  const { changePassword, logout } = useAuth();
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const rules = passwordRules(pw);
  const mismatch = pw2 !== '' && pw !== pw2;
  const ready = rules.every((r) => r.ok) && pw === pw2;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!ready) return;
    setError('');
    setIsLoading(true);
    try {
      await changePassword(pw);
    } catch (err) {
      setError(err.message || 'Не удалось сохранить пароль');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <LoginShell title="Придумайте пароль">
      <div style={hintStyle}>По нему вы будете входить в панель дальше.</div>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="form-label" htmlFor="nf-new-password">Новый пароль</label>
          <PasswordInput id="nf-new-password" value={pw} onChange={setPw} placeholder="Новый пароль" autoComplete="new-password" autoFocus />
          <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0', fontSize: 13 }}>
            {rules.map((r) => (
              <li key={r.text} style={{ color: r.ok ? 'var(--primary-color)' : '#999' }}>
                {r.ok ? '✓' : '•'} {r.text}
              </li>
            ))}
          </ul>
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="nf-new-password2">Повторите пароль</label>
          <PasswordInput id="nf-new-password2" value={pw2} onChange={setPw2} placeholder="Ещё раз" autoComplete="new-password" />
        </div>
        {(error || mismatch) && <div style={errorStyle}>{error || 'Пароли не совпадают'}</div>}
        <button type="submit" className="btn btn-primary btn-block" disabled={!ready || isLoading}>
          {isLoading ? 'Сохраняем...' : 'Сохранить и войти'}
        </button>
        <button type="button" className="btn btn-ghost btn-block" style={{ marginTop: 8 }} onClick={logout}>
          Выйти
        </button>
      </form>
    </LoginShell>
  );
}

function Login() {
  const { login, mustChangePassword } = useAuth();
  // login — обычный вход; recover — куда прислать временный пароль; sent — ввод временного
  const [mode, setMode] = useState('login');
  const [loginValue, setLoginValue] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // Секунды паузы после трёх неверных попыток подряд (сервер отвечает 429 + Retry-After)
  const [waitSec, setWaitSec] = useCountdown();
  // Каналы восстановления для логина: null — ещё не спрашивали
  const [channels, setChannels] = useState(null);
  const [sentChannel, setSentChannel] = useState(null);
  // Сколько ещё живёт временный пароль (3 минуты); 0 — «Отправить заново»
  const [tempLeft, setTempLeft] = useCountdown();

  if (mustChangePassword) {
    return <ChangePasswordForm />;
  }

  const goTo = (next) => {
    setError('');
    setPassword('');
    setChannels(null);
    setMode(next);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (waitSec > 0) return;
    setError('');
    setIsLoading(true);

    try {
      await login(loginValue.trim(), password);
    } catch (err) {
      if (err.retryAfter) {
        setWaitSec(err.retryAfter);
        setPassword('');
      } else {
        setError(mode === 'sent' ? 'Неверный временный пароль' : 'Неверный логин или пароль');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRecoverNext = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const data = await api.recoveryOptions(loginValue.trim());
      setChannels(data.channels || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = async (channel) => {
    setError('');
    setIsLoading(true);
    try {
      const data = await api.recoverySend(loginValue.trim(), channel);
      setSentChannel(channel);
      setTempLeft(data.expires_in || 180);
      setPassword('');
      setMode('sent');
    } catch (err) {
      setError(err.retryAfter
        ? `${err.message}. Повторите через ${formatWait(err.retryAfter)}`
        : err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const loginField = (
    <div className="form-group">
      <label className="form-label" htmlFor="nf-login">Логин</label>
      <div className="nf-input-wrap">
        <input
          id="nf-login"
          type="text"
          autoFocus={!loginValue}
          className="form-input"
          placeholder="Введите логин"
          value={loginValue}
          onChange={(e) => { setLoginValue(e.target.value); setChannels(null); }}
          autoComplete="username"
          required
        />
      </div>
    </div>
  );

  const lockedNote = waitSec > 0 && (
    <div style={errorStyle}>
      Три неверные попытки подряд. Повторите через {formatWait(waitSec)}
    </div>
  );

  if (mode === 'recover') {
    return (
      <LoginShell title="Восстановление пароля">
        <form onSubmit={handleRecoverNext}>
          {loginField}
          {channels === null && (
            <button type="submit" className="btn btn-primary btn-block" disabled={isLoading || !loginValue.trim()}>
              {isLoading ? 'Проверяем...' : 'Далее'}
            </button>
          )}
        </form>
        {channels !== null && channels.length > 0 && (
          <>
            <div style={hintStyle}>Куда прислать временный пароль? Он действует 3 минуты.</div>
            {channels.map((ch) => (
              <button
                key={ch}
                type="button"
                className="btn btn-primary btn-block"
                style={{ marginBottom: 8 }}
                disabled={isLoading}
                onClick={() => handleSend(ch)}
              >
                {`Прислать в ${CHANNEL_LABELS[ch] || ch}`}
              </button>
            ))}
          </>
        )}
        {channels !== null && channels.length === 0 && (
          <div style={hintStyle}>
            Для этого логина не указан Telegram или Max. Попросите владельца магазина
            добавить их в вашу карточку сотрудника.
          </div>
        )}
        {error && <div style={errorStyle}>{error}</div>}
        <button type="button" className="btn btn-ghost btn-block" style={{ marginTop: 8 }} onClick={() => goTo('login')}>
          Ко входу
        </button>
      </LoginShell>
    );
  }

  if (mode === 'sent') {
    const label = CHANNEL_LABELS[sentChannel] || sentChannel;
    return (
      <LoginShell title="Введите временный пароль">
        <div style={hintStyle}>
          {tempLeft > 0
            ? `Отправили в ${label}. Пароль действует ещё ${formatWait(tempLeft)}.`
            : 'Срок временного пароля истёк — отправьте новый.'}
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label" htmlFor="nf-temp-password">Временный пароль</label>
            <PasswordInput id="nf-temp-password" value={password} onChange={setPassword} placeholder={`Пароль из ${label}`} autoComplete="one-time-code" autoFocus />
          </div>
          {lockedNote}
          {error && tempLeft > 0 && <div style={errorStyle}>{error}</div>}
          {tempLeft > 0 ? (
            <button type="submit" className="btn btn-primary btn-block" disabled={isLoading || waitSec > 0}>
              {isLoading ? 'Вход...' : waitSec > 0 ? `Подождите ${formatWait(waitSec)}` : 'Войти'}
            </button>
          ) : (
            <button type="button" className="btn btn-primary btn-block" disabled={isLoading} onClick={() => handleSend(sentChannel)}>
              {isLoading ? 'Отправляем...' : 'Отправить заново'}
            </button>
          )}
        </form>
        <button type="button" className="btn btn-ghost btn-block" style={{ marginTop: 8 }} onClick={() => goTo('login')}>
          Ко входу
        </button>
      </LoginShell>
    );
  }

  return (
    <LoginShell title="Панель сотрудника">
      <form onSubmit={handleSubmit}>
        {loginField}

        <div className="form-group">
          <label className="form-label" htmlFor="nf-password">Пароль</label>
          <PasswordInput id="nf-password" value={password} onChange={setPassword} placeholder="Введите пароль" autoComplete="current-password" />
        </div>

        {lockedNote || (error && <div style={errorStyle}>{error}</div>)}

        <button
          type="submit"
          className="btn btn-primary btn-block"
          disabled={isLoading || waitSec > 0}
        >
          {isLoading ? 'Вход...' : waitSec > 0 ? `Подождите ${formatWait(waitSec)}` : 'Войти'}
        </button>
      </form>
      <button type="button" className="btn btn-ghost btn-block" style={{ marginTop: 8 }} onClick={() => goTo('recover')}>
        Забыли пароль?
      </button>
    </LoginShell>
  );
}

export default Login;
